import { BillingConfig, BillingProviderSchema } from '@kit/billing';
import { ProductPlanLineHandler } from './billing.schema';

const provider = BillingProviderSchema.parse(
  process.env.NEXT_PUBLIC_BILLING_PROVIDER,
);

type ProductId = 'starter' | 'pro' | 'enterprise';

export function getFeatures(productId: ProductId, interval?: 'monthly' | 'yearly'): [string, ...string[]] {
  const baseFeatures = [
    `Pay as you go computing power`,
    `Pay as you go unlimited page creation`,
    `Public or internal team only page`, 
    `Custom customer service/support SOP`, 
    `Custom unlimited RAG knowledge base`, 
    `Unlimited links and products catalog`, 
    `Always online 24/7`,
    `Multi language ability`,
    `Multi-tenancy`,
    `Unlimited organizations/teams`,
    `Unlimited members`,
    `Member roles and permissions`,
    `Transfer ownership`,
  ];

  if (interval) {
    const computingPower: Record<ProductId, string> = {
      'starter': interval === 'monthly' ? '350,000' : '4,200,000',
      'pro': interval === 'monthly' ? '1,050,000' : '12,600,000',
      'enterprise': interval === 'monthly' ? '2,100,000' : '25,200,000',
    };

    return [
      `+ ${computingPower[productId]} Computing Power per ${interval === 'yearly' ? 'year' : 'month'}`,
      ...baseFeatures
    ];
  } else {
    return baseFeatures as [string, ...string[]];
  }
}

export const billingConfig: BillingConfig = {
  // also update config.billing_provider in the DB to match the selected
  provider,
  // products configuration
  products: [
    {
      id: 'topup-cp',
      name: 'Top Up Computing Power',
      description: 'Top up your computing power',
      currency: 'USD',
      badge: `Value`,
      plans: [
        {
          name: 'Top Up 50K Computing Power',
          id: 'topup-50k-plan',
          paymentType: 'one-time',
          lineItems: [
            {
              id: 'price_1RZo6YDS2p4LZPffDuYbdQfb',
              name: 'Topup 50K Computing Power',
              cost: 4,
              type: 'flat' as const,
              features: ['+50,000 Computing Power'],
            },
          ],
        },
      ],
      features: ['+50,000 Computing Power'],
    },
    {
      id: 'starter',
      name: 'Starter',
      description: 'The perfect plan to get started',
      currency: 'USD',
      badge: `Value`,
      plans: [
        {
          name: 'Starter Monthly Plan',
          id: 'starter-monthly-plan',
          paymentType: 'recurring',
          interval: 'month',
          lineItems: [
            {
              id: 'price_1RZo7eDS2p4LZPffznWmqE4a',
              name: 'Base',
              cost: 25,
              type: 'flat' as const,
              features: getFeatures('starter', 'monthly'),
            },
          ],
        },
        {
          name: 'Starter Yearly Plan',
          id: 'starter-yearly-plan',
          paymentType: 'recurring',
          interval: 'year',
          lineItems: [
            {
              id: 'price_1RZo8yDS2p4LZPff2jsM9faA',
              name: 'Base',
              cost: 255,
              type: 'flat' as const,
              features: getFeatures('starter', 'yearly'),
            },
          ],
        },
      ],
      features: getFeatures('starter'),
    },
    {
      id: 'pro',
      name: 'Pro',
      badge: `Popular`,
      highlighted: true,
      description: 'The perfect plan for professionals',
      currency: 'USD',
      plans: [
        {
          name: 'Pro Monthly Plan',
          id: 'pro-monthly-plan',
          paymentType: 'recurring',
          interval: 'month',
          lineItems: [
            {
              id: 'price_1RZoABDS2p4LZPffq47yWn8w',
              name: 'Base',
              cost: 75,
              type: 'flat',
              features: getFeatures('pro', 'monthly'),
            },
          ],
        },
        {
          name: 'Pro Yearly Plan',
          id: 'pro-yearly-plan',
          paymentType: 'recurring',
          interval: 'year',
          lineItems: [
            {
              id: 'price_1RZoBPDS2p4LZPffFjdzj7Ix',
              name: 'Base',
              cost: 765,
              type: 'flat',
              features: getFeatures('pro', 'yearly'),
            },
          ],
        },
      ],
      features: getFeatures('pro'),
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'The perfect plan for enterprises',
      currency: 'USD',
      plans: [
        {
          name: 'Enterprise Monthly Plan',
          id: 'enterprise-monthly-plan',
          paymentType: 'recurring',
          interval: 'month',
          lineItems: [
            {
              id: 'price_1RZoDtDS2p4LZPffwlQ0tBw5',
              name: 'Base',
              cost: 150,
              type: 'flat',
              features: getFeatures('enterprise', 'monthly'),
            },
          ],
        },
        {
          name: 'Enterprise Yearly Plan',
          id: 'enterprise-yearly-plan',
          paymentType: 'recurring',
          interval: 'year',
          lineItems: [
            {
              id: 'price_1RZoE9DS2p4LZPffGtztVw90',
              name: 'Base',
              cost: 1530,
              type: 'flat',
              features: getFeatures('enterprise', 'yearly'),
            },
          ],
        },
      ],
      features: getFeatures('enterprise'),
    },
  ],
};

export function getBenefitByLineId(lineId: string, params: ProductPlanLineHandler) {
  switch (lineId) {
    case 'price_1RZo6YDS2p4LZPffDuYbdQfb':
      params.onPaymentTopup50kItem();
      break;
    case 'price_1RZo7eDS2p4LZPffznWmqE4a':
      params.onPaymentStarterMonthlyItem();
      break;
    case 'price_1RZo8yDS2p4LZPff2jsM9faA':
      params.onPaymentStarterYearlyItem();
      break;
    case 'price_1RZoABDS2p4LZPffq47yWn8w':
      params.onPaymentProMonthlyItem();
      break;
    case 'price_1RZoBPDS2p4LZPffFjdzj7Ix':
      params.onPaymentProYearlyItem();
      break;
    case 'price_1RZoDtDS2p4LZPffwlQ0tBw5':
      params.onPaymentEnterpriseMonthlyItem();
      break;
    case 'price_1RZoE9DS2p4LZPffGtztVw90':
      params.onPaymentEnterpriseYearlyItem();
      break;
    default:
      params.onPayment(lineId);
  }
}
